import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import callApi from "helpers/callApi";
import { getApiErrorMessage } from "utils/errors";
import {
  mapVerificationCenterEntity,
  mapVerificationCenterList,
} from "./verificationProduct";

const UPLOAD_FAILED_FALLBACK = "Upload failed";

function isProbablyHtmlErrorBody(text) {
  const s = String(text ?? "").trim();
  if (!s) return false;
  return /^<!doctype html/i.test(s) || /^<html[\s>]/i.test(s);
}

function extractUploadErrorMessage(body, fallback = UPLOAD_FAILED_FALLBACK) {
  if (typeof body === "string" && isProbablyHtmlErrorBody(body)) {
    return fallback;
  }
  const extracted = getApiErrorMessage({ response: { data: body } }, fallback);
  if (typeof extracted !== "string") return fallback;
  const trimmed = extracted.trim();
  return trimmed || fallback;
}

const getAvailableDocuments = (required_documents = []) => {
  return (required_documents || []).filter((doc) => {
    if (typeof doc === "object" && doc !== null) return !doc.is_uploaded;
    return true;
  });
};

const getIsRequestCompleted = (details) => {
  const mode = details?.requirement_mode || "ALL_REQUIRED";
  const docs = details?.required_documents || [];

  if (!docs.length) return false;

  if (mode === "ANY_ONE") {
    return docs.some((d) => d && typeof d === "object" ? !!d.is_uploaded : false);
  }

  return docs.every((d) => d && typeof d === "object" ? !!d.is_uploaded : false);
};


export const fetchDocumentCategories = createAsyncThunk(
  "uploadDocuments/fetchDocumentCategories",
  async (_, { rejectWithValue }) => {
    try {
      const res = await callApi(
        "/verification_center/document-categories/",
        "GET"
      );

      const json = await res.json().catch(() => ({}));

      if (res.ok) return json;
      return rejectWithValue(res.errorMessage || "Error");
    } catch (err) {
      return rejectWithValue(err?.message || "Error");
    }
  }
);

export const fetchDocumentRequestDetails = createAsyncThunk(
  "uploadDocuments/fetchDocumentRequestDetails",
  async (id, { rejectWithValue }) => {
    try {
      const res = await callApi(
        `/verification_center/requests/${id}/`,
        "GET"
      );

      const json = await res.json().catch(() => ({}));

      if (res.ok) return mapVerificationCenterEntity(json);

      return rejectWithValue(res.errorMessage || "Error");

    } catch (err) {
      return rejectWithValue(err?.message || "Error");
    }
  }
);

export const createDocumentRequest = createAsyncThunk( 
  "uploadDocuments/createDocumentRequest",
  async ({ payload, template_file }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("payload", JSON.stringify(payload));

      if (template_file) {
        formData.append("template_file", template_file);
      }

      const res = await callApi(
        "/verification_center/requests/",
        "POST",
        formData,
        { contentType: "multipart/form-data" }
      );

      const json = await res.json().catch(() => ({}));
      
      if (res.ok) {
        return mapVerificationCenterEntity(json);
      }
      return rejectWithValue(res.errorMessage || "Error");
    } catch (err) {
      return rejectWithValue(err?.message || "Error");
    }
  }
);

export const uploadVerificationDocuments = createAsyncThunk(
  "uploadDocuments/uploadVerificationDocuments",
  async (
    { requestId, requirementMode, selectedDoc, files, filesByDoc },
    { rejectWithValue }
  ) => {
    try {
      const formData = new FormData();

      if (requirementMode === "ANY_ONE") {
        if (!selectedDoc || !files?.length) {
          return rejectWithValue("No files selected");
        }

        files.forEach((file) => {
          formData.append(selectedDoc, file);
        });
      } else {
        Object.entries(filesByDoc || {}).forEach(([docName, list]) => {
          (list || []).forEach((file) => {
            formData.append(docName, file);
          });
        });
      }

      const res = await callApi(
        `/verification_center/requests/${requestId}/upload/`,
        "POST",
        formData,
        { contentType: "multipart/form-data" }
      );

      if (!res.ok) {
        console.error("Verification Center document upload failed", {
          status: res.status,
          requestId,
        });
        return rejectWithValue(res.errorMessage || "Error");
      }

      let data = null;
      try {
        data = await res.json();
      } catch (_) {
        data = null;
      }

      const errorMessage =
        data && typeof data === "object" ? data.error_message : null;
      if (errorMessage != null && errorMessage !== "") {
        const message =
          typeof errorMessage === "string"
            ? errorMessage.trim()
            : extractUploadErrorMessage(errorMessage);
        return rejectWithValue(message || UPLOAD_FAILED_FALLBACK);
      }

      return data;
    } catch (e) {
      return rejectWithValue(e?.message || "Error");
    }
  }
);

export const fetchVerificationHistory = createAsyncThunk(
  "uploadDocuments/fetchVerificationHistory",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (filters.sku) {
        params.append("sku", filters.sku);
      }
      if (filters.product_id) {
        params.append("product_id", filters.product_id);
      }
      const queryString = params.toString();
      const url = queryString ? `/verification_center/history/?${queryString}` : "/verification_center/history/";
      
      const res = await callApi(url, "GET");

      const json = await res.json().catch(() => ({}));
      if (res.ok) return mapVerificationCenterList(json);

      return rejectWithValue(res.errorMessage || "Error");
    } catch (err) {
      return rejectWithValue(err?.message || "Error");
    }
  }
);

export const updateDocumentStatus = createAsyncThunk(
  "uploadDocuments/updateDocumentStatus",
  async ({ documentId, status, comment }, { rejectWithValue }) => {
    try {
      const res = await callApi(
        `/verification_center/uploaded-documents/${documentId}/status/`,
        "POST",
        {
          status,
          comment: comment || "",
        }
      );

      const json = await res.json().catch(() => ({}));

      if (res.ok) return mapVerificationCenterEntity(json);

      return rejectWithValue(res.errorMessage || "Error");
    } catch (err) {
      return rejectWithValue(err?.message || "Error");
    }
  }
);

export const markDocumentRequestReceived = createAsyncThunk(
  "uploadDocuments/markDocumentRequestReceived",
  async ({ requestId }, { rejectWithValue }) => {
    try {
      const res = await callApi(
        `/verification_center/requests/${requestId}/status/`,
        "POST",
        { status: "APPROVED" }
      );
      const json = await res.json().catch(() => ({}));

      if (res.ok) return mapVerificationCenterEntity(json);

      return rejectWithValue(res.errorMessage || "Error");
    } catch (err) {
      return rejectWithValue(getApiErrorMessage(err, "Error") || "Error");
    }
  }
);


const uploadDocumentsSlice = createSlice({
  name: "uploadDocuments",

  initialState: {
    categories: [],
    items: [],
    uploadedFiles: [],
    loading: false,
    createError: null,
    createSuccess: false,
    requestDetails: null,
    loadingDetails: false,
    availableDocuments: [],
    isRequestCompleted: false,

    uploadLoading: false,
    uploadError: null,
    uploadSuccess: null,

    verificationHistory: [],
    loadingHistory: false,
    historyError: null,

    updatingStatus: false,
    updateStatusError: null,
    updateStatusSuccess: null,

    markingRequestReceived: false,
    markRequestReceivedError: null,
    markRequestReceivedSuccess: false,
  },

  reducers: {
    clearUploadState(state) {
      state.categories = [];
      state.items = [];
      state.uploadedFiles = [];
      state.error = null;
      state.loading = false;
      state.createSuccess = false;
      state.requestDetails = null;
      state.loadingDetails = false;
      state.availableDocuments = [];
      state.isRequestCompleted = false;
    },
    clearCreateSuccess(state) {
      state.createSuccess = false;
    },  
    clearCreateError(state) {
      state.createError = null;
    },
    resetUploadFeedback(state) {
      state.uploadLoading = false;
      state.uploadError = null;
      state.uploadSuccess = null;
    },
    clearUpdateStatusFeedback(state) {
      state.updatingStatus = false;
      state.updateStatusError = null;
      state.updateStatusSuccess = null;
    },
    clearMarkRequestReceivedFeedback(state) {
      state.markRequestReceivedError = null;
      state.markRequestReceivedSuccess = false;
    }
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchDocumentCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(fetchDocumentCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload || [];
      })

      .addCase(fetchDocumentCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch document categories";
      })

      .addCase(createDocumentRequest.pending, (state) => {
        state.loading = true;
        state.createError = null;
        state.createSuccess = false;
      })

      .addCase(createDocumentRequest.fulfilled, (state) => {
        state.loading = false;
        state.createError = null;
        state.createSuccess = true;
      })

      .addCase(createDocumentRequest.rejected, (state, action) => {
        state.loading = false;
        state.createError = action.payload || "Failed to create request";
        state.createSuccess = false;
      })

      .addCase(fetchDocumentRequestDetails.pending, (state) => {
        state.loadingDetails = true;
        state.error = null;
        state.requestDetails = null;
        state.isRequestCompleted = false;
        state.availableDocuments = [];
      })

      .addCase(fetchDocumentRequestDetails.fulfilled, (state, action) => {
        state.loadingDetails = false;
        const details = action.payload || null;
        state.requestDetails = details;
        state.availableDocuments = getAvailableDocuments(details?.required_documents);
        state.isRequestCompleted = getIsRequestCompleted(details);
      })

      .addCase(fetchDocumentRequestDetails.rejected, (state, action) => {
        state.loadingDetails = false;
        state.error = action.payload || "Failed to load request details";
        state.availableDocuments = [];
        state.isRequestCompleted = false;
      })

      builder.addCase(uploadVerificationDocuments.pending, (state) => {
      state.uploadLoading = true;
      state.uploadError = null;
      state.uploadSuccess = null;
    });

    builder.addCase(uploadVerificationDocuments.fulfilled, (state, action) => {
      state.uploadLoading = false;
      state.uploadSuccess = action?.payload?.success_message;
      state.uploadError = action?.payload?.error_message;
    });

    builder.addCase(uploadVerificationDocuments.rejected, (state, action) => {
      state.uploadLoading = false;
      state.uploadError = action.payload || "Upload failed";
    })

    .addCase(fetchVerificationHistory.pending, (state) => {
      state.loadingHistory = true;
      state.historyError = null;
    })

    .addCase(fetchVerificationHistory.fulfilled, (state, action) => {
      state.loadingHistory = false;
      state.verificationHistory = action.payload || [];
      state.historyError = null;
    })

    .addCase(fetchVerificationHistory.rejected, (state, action) => {
      state.loadingHistory = false;
      state.historyError = action.payload || "Failed to fetch verification history";
      state.verificationHistory = [];
    })

    .addCase(updateDocumentStatus.pending, (state) => {
      state.updatingStatus = true;
      state.updateStatusError = null;
      state.updateStatusSuccess = null;
    })

    .addCase(updateDocumentStatus.fulfilled, (state, action) => {
      state.updatingStatus = false;
      state.updateStatusError = null;
      state.updateStatusSuccess = action.payload?.message || "Status updated successfully";
    })

    .addCase(updateDocumentStatus.rejected, (state, action) => {
      state.updatingStatus = false;
      state.updateStatusError = action.payload || "Failed to update document status";
      state.updateStatusSuccess = null;
    })

    .addCase(markDocumentRequestReceived.pending, (state) => {
      state.markingRequestReceived = true;
      state.markRequestReceivedError = null;
      state.markRequestReceivedSuccess = false;
    })

    .addCase(markDocumentRequestReceived.fulfilled, (state, action) => {
      state.markingRequestReceived = false;
      state.markRequestReceivedError = null;
      state.markRequestReceivedSuccess = true;

      const updatedRequest = action.payload;
      const requestId = updatedRequest?.id ?? action.meta.arg.requestId;
      if (
        state.requestDetails &&
        String(state.requestDetails.id) === String(requestId)
      ) {
        state.requestDetails = {
          ...state.requestDetails,
          ...updatedRequest,
        };
        state.availableDocuments = getAvailableDocuments(
          state.requestDetails.required_documents
        );
        state.isRequestCompleted = getIsRequestCompleted(state.requestDetails);
      }
    })

    .addCase(markDocumentRequestReceived.rejected, (state, action) => {
      state.markingRequestReceived = false;
      state.markRequestReceivedError =
        action.payload || "Failed to mark document request as received";
      state.markRequestReceivedSuccess = false;
    });
  },
});

export const { 
  clearUploadState, 
  clearCreateSuccess, 
  clearCreateError,
  resetUploadFeedback,
  clearUpdateStatusFeedback,
  clearMarkRequestReceivedFeedback
} = uploadDocumentsSlice.actions;

export default uploadDocumentsSlice.reducer;